<script setup lang="ts">
  import { FormFieldContainer, InputField, UiButton } from '@logistiek-zonder-papier/lzp-library'
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import type { PasswordRule } from '@/types/login-types.ts'
  import { useLoginStore } from '@/store/login.store.ts'

  const errorMessages = ref<string[]>([])
  const isSubmitting = ref(false)
  const success = ref(false)

  const firstPassword = ref<string>('')
  const secondPassword = ref<string>('')

  const router = useRouter()
  const loginStore = useLoginStore()

  const query = router.currentRoute.value.query['token']

  const token: string | undefined = Array.isArray(query) ? query[0]?.toString() : query?.toString()

  async function submit() {
    isSubmitting.value = true
    errorMessages.value = []

    if (token == null) {
      errorMessages.value.push('Incorrecte code')
      return
    }

    if (firstPassword.value !== secondPassword.value) {
      errorMessages.value.push('Uw wachtwoorden moeten gelijk zijn.')
    }

    const passwordRules: PasswordRule[] = loginStore.validatePassword(firstPassword.value)
    if (passwordRules.length > 0) {
      errorMessages.value.push(...passwordRules.map(rule => rule.label))
    }

    if (errorMessages.value.length > 0) {
      isSubmitting.value = false
      return
    }

    await loginStore.changePassword(token, firstPassword.value, secondPassword.value)

    success.value = true
    isSubmitting.value = false
  }
</script>

<template>
  <div class="login-layout">
    <div class="login-card">
      <section class="content-layout">
        <img
          id="logo"
          class="logo"
          src="/content/img/lzp_logo.png"
          width="200"
          height="132"
          alt="LZP logo"
        />
        <div v-if="!success" class="login-form">
          <FormFieldContainer label="Nieuw wachtwoord">
            <InputField
              id="first-password"
              v-model="firstPassword"
              type="password"
              placeholder="Voer uw nieuwe wachtwoord in"
              @keyup.enter="submit"
              required
            />
          </FormFieldContainer>

          <FormFieldContainer label="Herhaal wachtwoord">
            <InputField
              id="second-password"
              v-model="secondPassword"
              type="password"
              placeholder="Herhaal uw nieuwe wachtwoord"
              @keyup.enter="submit"
              required
            />
          </FormFieldContainer>
          <ul v-if="errorMessages.length > 0">
            <li v-for="errorMessage in errorMessages" :key="errorMessage" class="error-message">
              {{ errorMessage }}
            </li>
          </ul>
          <UiButton
            :disabled="isSubmitting"
            variant="prominent"
            type="submit"
            label="Verzenden"
            @click="submit"
          />
        </div>
        <div v-else class="login-form">
          <h4>Uw wachtwoord is gewijzigd</h4>
          <p>Uw wachtwoord is succesvol gewijzigd. U kunt nu inloggen met uw nieuwe wachtwoord.</p>
          <UiButton
            variant="prominent"
            type="submit"
            label="Terug naar inloggen"
            @click="$router.replace('/login')"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
  .logo {
    align-self: center;
  }

  .login-layout {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 100vh;
  }

  .login-card {
    border: 1px solid var(--colours-grey-3);
    border-radius: var(--components-card-and-modal-corners);
    background-color: var(--colours-grey-1);
    display: flex;
    width: var(--layout-8-col-text-max);
    padding: 0 var(--layout-side-padding-desktop);
    flex-direction: column;
  }

  .content-layout {
    display: flex;
    padding: var(--spacing-l) 0;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .login-form {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-s);
  }

  .error-message {
    color: var(--colours-support-negative);
  }
</style>
